import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/styles/use-finance-theme';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { resolveIcon } from '../../runtime/icons';
import { resolveTKey } from '../../i18n/resolve-key';
import { getRouteById, getRouteByPath } from '../../routes/registry';
import { useAccountMenu } from './use-account-menu';
import { StatusChip } from '../experience/StatusChip';

export function AccountMenu() {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    triggerRef,
    triggerId,
    menuId,
    anchorEl,
    open,
    viewModel,
    contextSwitchState,
    logoutPending,
    delegationExitPending,
    navigationLoading,
    navigationError,
    refetchNavigation,
    handleOpen,
    handleClose,
    handleLogout,
    handleExitDelegation,
    handleSwitchContext,
  } = useAccountMenu();

  const currentRouteId = getRouteByPath(location.pathname)?.routeId;
  const showWorkspaceSummary = Boolean(
    viewModel.workspace &&
    ((viewModel.workspace.scopeKind !== 'ORGANIZATION' && viewModel.workspace.organizationName) ||
      (viewModel.workspace.scopeKind !== 'ORGANIZATION' && viewModel.workspace.branchName) ||
      viewModel.workspace.clientName ||
      viewModel.workspace.portfolioName),
  );
  const sectionLabelSx = {
    px: 1,
    pb: 0.5,
    display: 'block',
    color: 'text.secondary',
    letterSpacing: '0.08em',
  } as const;

  return (
    <>
      <Tooltip title={viewModel.identity.displayName} enterDelay={600}>
        <IconButton
          ref={triggerRef}
          id={triggerId}
          data-testid="account-menu-button"
          onClick={handleOpen}
          aria-label={t('shell:userMenu.triggerLabel')}
          aria-haspopup="menu"
          aria-controls={open ? menuId : undefined}
          aria-expanded={open ? 'true' : undefined}
          sx={{
            p: 0.25,
            border: 1,
            borderColor: open ? 'primary.main' : 'transparent',
          }}
        >
          <Avatar
            alt={viewModel.identity.displayName}
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.8rem',
              bgcolor: navigationLoading ? 'action.disabledBackground' : 'primary.main',
              color: 'primary.contrastText',
            }}
            data-testid="account-menu-avatar"
          >
            {navigationLoading
              ? <CircularProgress size={16} color="inherit" aria-hidden="true" />
              : viewModel.identity.initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: { 'aria-labelledby': triggerId, role: 'menu' },
          paper: {
            sx: {
              width: theme.financeLayout.shell.accountMenuWidth,
              maxWidth: theme.financeLayout.shell.accountMenuMaxWidth,
              maxHeight: theme.financeLayout.shell.accountMenuMaxHeight,
              overflowY: 'auto',
              mt: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              boxShadow: theme.shadows[8],
            },
          },
        }}
        data-testid="account-menu"
      >
        {viewModel.switchError && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert severity="error" sx={{ py: 0.5 }}>
              {t('shell:contextSelector.switchError')}
            </Alert>
          </Box>
        )}

        <Box
          sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider' }}
          data-testid="account-menu-identity"
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 46,
                height: 46,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              {viewModel.identity.initials}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap data-testid="avatar-user-name">
                {viewModel.identity.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap data-testid="avatar-user-email">
                {viewModel.identity.email}
              </Typography>
              {viewModel.personaLabel && (
                <Chip
                  label={viewModel.personaLabel}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.75, maxWidth: '100%' }}
                  data-testid="avatar-persona"
                />
              )}
            </Box>
          </Box>

          {viewModel.workspace && showWorkspaceSummary && (
            <Box
              data-testid="avatar-context-summary"
              sx={{
                mt: 1.5,
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: 'action.hover',
              }}
            >
              <BusinessOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.15 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {viewModel.workspace.scopeKind !== 'ORGANIZATION' && viewModel.workspace.organizationName && (
                  <Typography variant="body2" sx={{ fontWeight: 650 }} noWrap>
                    {viewModel.workspace.organizationName}
                  </Typography>
                )}
                {viewModel.workspace.scopeKind !== 'ORGANIZATION' && viewModel.workspace.branchName && (
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {viewModel.workspace.branchName}
                  </Typography>
                )}
                {viewModel.workspace.clientName && (
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {viewModel.workspace.clientName}
                  </Typography>
                )}
                {viewModel.workspace.portfolioName && (
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {viewModel.workspace.portfolioName}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {viewModel.delegated && (
            <StatusChip
              status="warning"
              label={t('shell:userMenu.delegated')}
              variant="outlined"
              sx={{ mt: 1.25 }}
              data-testid="avatar-delegated-indicator"
            />
          )}
        </Box>

        {viewModel.contexts.length > 1 && (
          <Box sx={{ px: 1, py: 1 }}>
            <Typography variant="overline" sx={sectionLabelSx}>
              {t('shell:userMenu.switchContext')}
            </Typography>
            {viewModel.contexts.map(context => (
              <MenuItem
                key={`${context.membershipId}-${context.branchId}`}
                selected={context.selected}
                disabled={context.disabled || contextSwitchState === 'pending'}
                onClick={() => void handleSwitchContext(context.membershipId, context.branchId)}
                data-testid={`account-menu-context-${context.membershipId}-${context.branchId}`}
                sx={{ borderRadius: 1, mx: 0.5 }}
              >
                <ListItemIcon>
                  {context.selected ? <CheckIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
                </ListItemIcon>
                <ListItemText
                  primary={context.organizationName}
                  secondary={context.branchName}
                  slotProps={{
                    primary: { noWrap: true, sx: { fontWeight: 600 } },
                    secondary: { noWrap: true },
                  }}
                />
              </MenuItem>
            ))}
            <Divider sx={{ mt: 1 }} />
          </Box>
        )}

        <Box sx={{ px: 1, py: 1 }}>
          <Typography variant="overline" sx={sectionLabelSx}>
            {t('shell:userMenu.accountActions')}
          </Typography>

          {navigationLoading && (
            <Box role="status" aria-label={t('common:loading')} sx={{ display: 'flex', justifyContent: 'center', py: 2 }} data-testid="account-menu-navigation-loading">
              <CircularProgress size={20} aria-hidden="true" />
            </Box>
          )}

          {!navigationLoading && navigationError && (
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="body2" color="text.secondary" data-testid="account-menu-navigation-error">
                {t('shell:userMenu.navigationRequestFailed')}
              </Typography>
              <Button size="small" startIcon={<RefreshIcon />} onClick={() => void refetchNavigation()} sx={{ mt: 1 }} data-testid="account-menu-retry" aria-label={t('shell:userMenu.retryNavigation')}>
                {t('common:retry')}
              </Button>
            </Box>
          )}

          {!navigationLoading && !navigationError && viewModel.actions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.5 }} data-testid="account-menu-empty-actions">
              {t('shell:userMenu.actionsConfiguredUnavailable')}
            </Typography>
          )}

          {!navigationLoading && !navigationError && viewModel.actions.map(action => {
            const route = getRouteById(action.routeId);
            if (!route) {
              return (
                <Alert key={action.id} severity="error" sx={{ mx: 1, my: 0.5 }}>
                  {t('shell:userMenu.navigationRequestFailed')}
                </Alert>
              );
            }
            const current = currentRouteId ? action.activeRouteIds.includes(currentRouteId) : false;
            const Icon = resolveIcon(action.iconKey) ?? SettingsIcon;
            const available = action.available;
            const disabledReason = action.disabledReasonKey
              ? t(resolveTKey(action.disabledReasonKey))
              : undefined;

            return (
              <MenuItem
                key={action.id}
                disabled={!available || logoutPending || delegationExitPending}
                onClick={() => {
                  if (!available) return;
                  handleClose();
                  navigate(route.path);
                }}
                data-testid={`account-menu-${action.routeId}`}
                sx={{ borderRadius: 1, mx: 0.5, py: 1.1 }}
              >
                <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary={action.label}
                  secondary={disabledReason || action.description}
                  slotProps={{
                    primary: { noWrap: true, sx: { fontWeight: 650 } },
                    secondary: { noWrap: false, sx: { lineHeight: 1.35, overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word' } },
                  }}
                />
                {available && (current ? <CheckIcon fontSize="small" color="success" /> : <ChevronRightIcon fontSize="small" color="action" />)}
              </MenuItem>
            );
          })}
        </Box>

        {viewModel.delegated && (
          <>
            <Divider />
            <Box sx={{ px: 1, py: 1 }}>
              <MenuItem
                onClick={() => void handleExitDelegation()}
                disabled={!viewModel.canExitDelegation || delegationExitPending || logoutPending}
                data-testid="account-menu-exit-delegation"
                sx={{ borderRadius: 1, mx: 0.5 }}
              >
                <ListItemIcon>
                  {delegationExitPending ? <CircularProgress size={18} /> : <ExitToAppIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={t('shell:delegationBanner.exitLabel')}
                  secondary={viewModel.exitDelegationDisabledReasonKey
                    ? t(resolveTKey(viewModel.exitDelegationDisabledReasonKey))
                    : undefined}
                  slotProps={{ primary: { sx: { fontWeight: 650 } } }}
                />
              </MenuItem>
            </Box>
          </>
        )}

        <Divider />

        <Box sx={{ px: 1, py: 1 }}>
          <MenuItem
            onClick={() => void handleLogout()}
            disabled={logoutPending || delegationExitPending}
            aria-label={t('settings:security.logout')}
            data-testid="account-menu-logout"
            sx={{
              color: 'error.main',
              borderRadius: 1,
              mx: 0.5,
              '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {logoutPending ? <CircularProgress size={18} color="inherit" /> : <LogoutIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={t('settings:security.logout')} slotProps={{ primary: { sx: { fontWeight: 700 } } }} />
          </MenuItem>
        </Box>
      </Menu>

      <Box
        aria-live="polite"
        data-testid="account-menu-context-switch-state"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        {contextSwitchState}
      </Box>
    </>
  );
}
